import { useEffect, useState } from 'react';
import { Save, X } from 'lucide-react';
import EquipePlanningPanel from './EquipePlanningPanel';
import MemberWeekAgenda from './MemberWeekAgenda';
import PractitionerPhotoControl from './PractitionerPhotoControl';
import {
  MemberAvatar,
  MemberBadge,
  MemberTabs,
  IntervenantToggle,
  AdminAccessSection,
  PrestationsSummary,
  SectionCard,
  NonIntervenantBlock,
  ROLE_LABELS,
  PRESTATION_CAPABILITY_LABEL,
  NO_PRESTATION_CAPABILITY_LABEL,
} from './equipeUi';

const TABS = [
  { id: 'general', label: 'Général' },
  { id: 'prestations', label: 'Prestations' },
  { id: 'planning', label: 'Planning' },
  { id: 'absences', label: 'Absences' },
  { id: 'acces', label: 'Accès Clickorico' },
];

export default function TeamMemberPanel({
  member,
  activeTab,
  onTabChange,
  generalForm,
  onGeneralFormChange,
  adminForm,
  onAdminFormChange,
  onSaveGeneral,
  commercantId,
  onPractitionerPhotoChange,
  photoSaving = false,
  onSaveAdmin,
  onSavePrestationCapability,
  onManagePrestations,
  prestations,
  saving,
  canManageRoles,
  weekOffset,
  onWeekOffsetChange,
  horairesConfig,
  absences = [],
  rdvList = [],
  weekLoading = false,
  onRdvClick,
  onPlanningChanged,
  onClose,
  layout = 'inline',
}) {
  const [planningView, setPlanningView] = useState('horaires');

  useEffect(() => {
    setPlanningView('horaires');
  }, [member?.id]);

  if (!member) {
    return (
      <div className="flex h-full min-h-[320px] flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white p-6 text-center">
        <p className="text-sm font-medium text-gray-600">Sélectionnez un membre</p>
        <p className="mt-1 text-xs text-gray-400">La fiche s&apos;affiche ici sans quitter la vue équipe.</p>
      </div>
    );
  }

  const protections = member.protections || {};
  const instructeurId = member.instructeurId || member.instructeur?.id;
  const isIntervenant = member.isIntervenant;
  const selectedPrestationIds = member.instructeur?.prestationIds || [];
  const fonction = member.fonctionMetier?.trim();
  const isGerant = protections.isGerant || member.roleInterne === 'GERANT';
  const isDrawer = layout === 'drawer';
  const wideLayout = isDrawer;

  return (
    <div
      className={`flex h-full flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-lg ring-1 ring-violet-100 ${
        layout === 'drawer' ? 'max-h-none min-h-0' : 'max-h-[calc(100vh-8rem)]'
      }`}
    >
      <div className="shrink-0 border-b border-gray-100 bg-gradient-to-br from-violet-50/80 via-white to-white px-4 py-4">
        <div className="flex items-start gap-3">
          <MemberAvatar member={member} size="lg" className="ring-2 ring-white shadow-md" />
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold leading-tight text-gray-900">
              {member.prenom} {member.nom}
            </h2>
            {fonction && <p className="mt-0.5 text-sm font-medium text-violet-700">{fonction}</p>}
            <div className="mt-2 flex flex-wrap gap-1">
              {isGerant && <MemberBadge label="Gérante" className="bg-violet-100 text-violet-800" />}
              {!isGerant && member.hasAdminAccess && member.roleInterne && (
                <MemberBadge label={ROLE_LABELS[member.roleInterne]} className="bg-blue-100 text-blue-800" />
              )}
              {isIntervenant && (
                <MemberBadge label={PRESTATION_CAPABILITY_LABEL} className="bg-emerald-50 text-emerald-800" />
              )}
              <MemberBadge
                label={member.actif !== false ? 'Actif' : 'Inactif'}
                className={
                  member.actif !== false
                    ? 'bg-green-100 text-green-800'
                    : 'bg-rose-100 text-rose-800'
                }
              />
            </div>
          </div>
          {onClose && (
            <button type="button" onClick={onClose} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100">
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      <div className="shrink-0 border-b border-gray-100 px-2 py-2">
        <MemberTabs tabs={TABS} activeTab={activeTab} onChange={onTabChange} />
      </div>

      <div
        className={`min-h-0 flex-1 overflow-y-auto py-4 ${isDrawer ? 'px-5 sm:px-8' : 'px-4'}`}
      >
        {activeTab === 'general' && (() => {
          const hasInstructeurProfile = Boolean(
            member?.instructeurId || member?.instructeur?.id || member?.kind === 'INSTRUCTEUR_ONLY',
          );
          const showPhoto =
            hasInstructeurProfile && Boolean(onPractitionerPhotoChange);
          const canToggleIntervenant = hasInstructeurProfile && !protections.isSelf;
          const canToggleAccount =
            !hasInstructeurProfile &&
            Boolean(member?.hasAdminAccess) &&
            !protections.isSelf &&
            !protections.isGerant;
          const showStatusToggle = canToggleIntervenant || canToggleAccount;

          const fieldClass =
            'w-full min-w-0 rounded-lg border border-gray-200 px-3 py-2 text-sm';
          const labelClass = 'mb-1 block text-xs font-medium text-gray-600';

          return (
            <div className="space-y-4">
              <SectionCard title="Fiche collaborateur">
                <div
                  className={`grid min-w-0 gap-6 lg:gap-8 ${
                    showPhoto
                      ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-[minmax(0,7.5rem)_minmax(0,1fr)_minmax(0,1fr)] xl:items-start'
                      : 'grid-cols-1 md:grid-cols-2'
                  }`}
                >
                  {showPhoto && (
                    <div className="flex justify-center md:justify-start xl:justify-center">
                      <PractitionerPhotoControl
                        member={member}
                        commercantId={commercantId}
                        onPhotoChange={(url) => onPractitionerPhotoChange(url)}
                        saving={photoSaving}
                        variant="inline"
                      />
                    </div>
                  )}

                  <div className="min-w-0 space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                      Identité et coordonnées
                    </p>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="min-w-0">
                        <label className={labelClass}>Prénom</label>
                        <input
                          value={generalForm.prenom}
                          onChange={(e) =>
                            onGeneralFormChange({ ...generalForm, prenom: e.target.value })
                          }
                          className={fieldClass}
                        />
                      </div>
                      <div className="min-w-0">
                        <label className={labelClass}>Nom</label>
                        <input
                          value={generalForm.nom}
                          onChange={(e) =>
                            onGeneralFormChange({ ...generalForm, nom: e.target.value })
                          }
                          className={fieldClass}
                        />
                      </div>
                      <div className="min-w-0">
                        <label className={labelClass}>Téléphone</label>
                        <input
                          type="tel"
                          value={generalForm.telephone}
                          onChange={(e) =>
                            onGeneralFormChange({ ...generalForm, telephone: e.target.value })
                          }
                          className={fieldClass}
                        />
                      </div>
                      <div className="min-w-0">
                        <label className={labelClass}>Email</label>
                        {member.hasAdminAccess ? (
                          <>
                            <input
                              type="email"
                              value={generalForm.email}
                              disabled
                              title={generalForm.email || undefined}
                              className="w-full min-w-0 truncate rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-600"
                            />
                            <p className="mt-1 text-[11px] leading-snug text-gray-500">
                              Compte Clickorico — non modifiable ici.
                            </p>
                          </>
                        ) : (
                          <input
                            type="email"
                            value={generalForm.email}
                            title={generalForm.email || undefined}
                            onChange={(e) =>
                              onGeneralFormChange({ ...generalForm, email: e.target.value })
                            }
                            placeholder="Optionnel"
                            className={`${fieldClass} truncate`}
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  <div
                    className={`min-w-0 space-y-3 ${
                      showPhoto ? 'md:col-span-2 xl:col-span-1' : ''
                    }`}
                  >
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                      Informations professionnelles
                    </p>
                    <div>
                      <label className={labelClass}>Fonction / métier</label>
                      <input
                        value={generalForm.fonctionMetier}
                        onChange={(e) =>
                          onGeneralFormChange({ ...generalForm, fonctionMetier: e.target.value })
                        }
                        placeholder="Ex. Esthéticienne, Technicien piscine…"
                        className={fieldClass}
                      />
                    </div>
                    {showStatusToggle && (
                      <div className="rounded-lg border border-gray-100 bg-gray-50/80 px-2.5 py-2">
                        <label className="flex cursor-pointer items-start gap-2 text-sm leading-snug">
                          <input
                            type="checkbox"
                            checked={generalForm.actif}
                            onChange={(e) =>
                              onGeneralFormChange({ ...generalForm, actif: e.target.checked })
                            }
                            className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300"
                          />
                          <span className="min-w-0">
                            <span className="font-medium text-gray-800">
                              {canToggleIntervenant
                                ? 'Actif (prestations)'
                                : 'Compte Clickorico actif'}
                            </span>
                            {canToggleIntervenant && (
                              <span className="mt-0.5 block text-[11px] leading-tight font-normal text-gray-500">
                                Désactive la dispo. RDV, sans impact sur le compte Clickorico.
                              </span>
                            )}
                          </span>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </SectionCard>

              {protections.isGerant && (
                <p className="text-xs text-amber-700">
                  Compte gérant : la désactivation du compte propriétaire est protégée.
                </p>
              )}

              <button
                type="button"
                onClick={onSaveGeneral}
                disabled={saving}
                className="inline-flex w-full max-w-xs items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50 sm:w-auto"
              >
                <Save className="h-4 w-4" />
                Enregistrer
              </button>
            </div>
          );
        })()}

        {activeTab === 'prestations' &&
          (isDrawer ? (
            <div className="grid gap-4 lg:grid-cols-[minmax(0,340px)_1fr] lg:items-start">
              <SectionCard>
                <IntervenantToggle
                  value={isIntervenant}
                  onChange={onSavePrestationCapability}
                  disabled={saving}
                  canManage={canManageRoles}
                />
                {!isIntervenant && (
                  <p className="mt-4 text-sm text-gray-500">{NO_PRESTATION_CAPABILITY_LABEL}.</p>
                )}
              </SectionCard>
              {isIntervenant && (
                <SectionCard title="Prestations attribuées">
                  <PrestationsSummary
                    prestations={prestations}
                    selectedIds={selectedPrestationIds}
                    onManage={onManagePrestations}
                  />
                </SectionCard>
              )}
            </div>
          ) : (
            <SectionCard>
              <IntervenantToggle
                value={isIntervenant}
                onChange={onSavePrestationCapability}
                disabled={saving}
                canManage={canManageRoles}
              />
              {isIntervenant ? (
                <div className="mt-6 border-t border-gray-100 pt-6">
                  <PrestationsSummary
                    prestations={prestations}
                    selectedIds={selectedPrestationIds}
                    onManage={onManagePrestations}
                  />
                </div>
              ) : (
                <p className="mt-4 text-sm text-gray-500">{NO_PRESTATION_CAPABILITY_LABEL}.</p>
              )}
            </SectionCard>
          ))}

        {activeTab === 'planning' && (
          <div className="space-y-3">
            {!isIntervenant ? (
              <p className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-600">
                Cette personne ne réalise pas de prestations.
              </p>
            ) : (
              <>
                <div className="flex rounded-xl bg-gray-100 p-1">
                  {[
                    { id: 'horaires', label: 'Horaires' },
                    { id: 'semaine', label: 'Semaine' },
                  ].map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => setPlanningView(v.id)}
                      className={`flex-1 rounded-lg px-2 py-1.5 text-xs font-semibold ${
                        planningView === v.id ? 'bg-white text-violet-700 shadow-sm' : 'text-gray-600'
                      }`}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
                {planningView === 'horaires' ? (
                  <EquipePlanningPanel
                    instructeurId={instructeurId}
                    mode="planning"
                    onPlanningChanged={onPlanningChanged}
                    wideLayout={wideLayout}
                  />
                ) : (
                  <MemberWeekAgenda
                    member={member}
                    weekOffset={weekOffset}
                    onWeekOffsetChange={onWeekOffsetChange}
                    horairesConfig={horairesConfig}
                    absences={absences}
                    rdvList={rdvList}
                    onRdvClick={onRdvClick}
                    loading={weekLoading}
                    wideLayout={wideLayout}
                  />
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'absences' && (
          <SectionCard title="Absences" className={isDrawer ? 'max-w-none' : undefined}>
            {!isIntervenant ? (
              <NonIntervenantBlock onActivate={() => onTabChange('prestations')} />
            ) : (
              <EquipePlanningPanel
                instructeurId={instructeurId}
                mode="absences"
                onPlanningChanged={onPlanningChanged}
                wideLayout={wideLayout}
              />
            )}
          </SectionCard>
        )}

        {activeTab === 'acces' && (
          <SectionCard>
            <AdminAccessSection
              member={member}
              form={adminForm}
              onChange={onAdminFormChange}
              onSave={onSaveAdmin}
              saving={saving}
              canManage={canManageRoles}
            />
          </SectionCard>
        )}
      </div>
    </div>
  );
}

export { TABS as TEAM_MEMBER_TABS };
